/* shared.jsx — Componentes compartidos: contador, progreso, donación, ticker, pagos */

import React from 'react'
import { MERCADOPAGO_PROCESSING_FEE_RATE } from './donation/processingFee.js'
import { isPageVisible, onPageVisibilityChange } from '../lib/pageVisibility.js'

const { useState: useStateS, useEffect: useEffectS, useRef: useRefS, useMemo: useMemoS } = React;

// ────────────────────────────────────────────────────────────
// Format helpers
// ────────────────────────────────────────────────────────────
function fmtCLP(n) {
  return n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
}
function fmtCLPshort(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1000).toFixed(0)}K`;
  return `${n}`;
}
// Convierte un monto en CLP a altura que aporta en la escala virtual
// (meta $800M = 60 m = edificio de 20 pisos × 3 m)
const META_CLP    = 800_000_000;
const META_HEIGHT_M = 32;          // alcance real del carro escala mecánica
const FLOORS_TOTAL  = 11;          // ~3 m por piso → 11 pisos

function heightCmFromAmount(amount) {
  return (amount / META_CLP) * META_HEIGHT_M * 100; // cm
}

function fmtHeight(amount) {
  const cm = heightCmFromAmount(amount);
  if (cm < 0.1) {
    const mm = cm * 10;
    if (mm < 0.01) return "<0,01 mm";
    return `${mm.toFixed(2).replace(".", ",")} mm`;
  }
  if (cm < 1)  return `${(cm * 10).toFixed(1).replace(".", ",")} mm`;
  if (cm < 100) {
    const dec = cm < 10 ? 2 : 1;
    return `${cm.toFixed(dec).replace(".", ",")} cm`;
  }
  return `${(cm / 100).toFixed(2).replace(".", ",")} m`;
}

// Días restantes hasta el 15 de enero (fecha límite de la campaña),
// tomando el próximo 15 de enero a partir de "now".
function daysUntilJan15(now) {
  const today = new Date(now);
  let deadline = new Date(today.getFullYear(), 0, 15, 23, 59, 59, 999);
  if (deadline < today) deadline = new Date(today.getFullYear() + 1, 0, 15, 23, 59, 59, 999);
  return Math.max(0, Math.ceil((deadline - today) / 86_400_000));
}

// ────────────────────────────────────────────────────────────
// Counter — anima de un valor al siguiente
// ────────────────────────────────────────────────────────────
function AnimatedNumber({ value, duration = 1400, className = "" }) {
  const [display, setDisplay] = useStateS(value);
  const fromRef = useRefS(value);
  const startRef = useRefS(0);
  const rafRef = useRefS(0);

  useEffectS(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    startRef.current = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startRef.current) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (to - from) * eased);
      setDisplay(v);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return <span className={className}>{fmtCLP(display)}</span>;
}

// ────────────────────────────────────────────────────────────
// Big counter block — el contador grande del hero
// ────────────────────────────────────────────────────────────
function MetaCounter({ raised, goal, progress, donors, daysLeft }) {
  const pct = Math.min(100, (raised / goal) * 100);
  const pctCumplimiento = `${(progress * 100).toFixed(1).replace(".", ",")}%`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div className="kicker" style={{ textTransform: "none" }}>De la meta de $800.000.000, llevamos recaudado:</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginTop: 4 }}>
          <div className="big-counter tabular">
            <span className="currency">$</span>
            <AnimatedNumber value={raised} />
          </div>
        </div>
      </div>
      <div className="progress">
        <div className="progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="stat-row">
        <div>
          <div className="v tabular">{pctCumplimiento}</div>
          <div className="l">% de cumplimiento</div>
        </div>
        <div>
          <div className="v tabular">{fmtCLP(donors)}</div>
          <div className="l">aportes</div>
        </div>
        <div>
          <div className="v tabular">{daysLeft}</div>
          <div className="l">días restantes</div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Canales de donación — etiquetas para el feed en vivo
// ────────────────────────────────────────────────────────────
const CHANNEL_LABELS = {
  mercadopago: "Mercado Pago",
  transfer: "Transferencia",
  person: "Tarjeta",
  company: "Empresa",
};

// ────────────────────────────────────────────────────────────
// Ticker — feed en vivo de aportes
// ────────────────────────────────────────────────────────────
function LiveTicker({ items }) {
  const doubled = [...items, ...items];
  return (
    <div className="ticker">
      <div className="ticker-track">
        {doubled.map((d, i) => (
          <div className="ticker-item" key={i}>
            <span className="amt">${fmtCLP(d.amount)}</span>
            <span className="dot">·</span>
            <b>{d.name}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Live donations table (la lista detallada)
// ────────────────────────────────────────────────────────────
function LiveDonations({ items }) {
  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="donations-row-grid" style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
        <div className="kicker">Aporte</div>
        <div className="kicker">Método</div>
        <div className="kicker" style={{ textAlign: "right" }}>Monto</div>
      </div>
      {items.map((d, i) => {
        const isNew = i === 0;
        return (
          <div
            key={d.id}
            className={`donation-row donations-row-grid ${isNew ? "is-new" : ""}`}
            style={{
              padding: "16px 20px",
              borderBottom: i < items.length - 1 ? "1px solid var(--line)" : "none",
              alignItems: "center",
              transition: "background-color .8s",
            }}
          >
            {/* Confetti burst */}
            {isNew && (
              <div className="donation-confetti" key={`conf-${d.id}`} aria-hidden="true">
                {[
                  { c: "#E8A93B", dx: -80, dy: -22, dr: -45, d: 0 },
                  { c: "#E63946", dx: -68, dy: 14, dr: 30, d: 0.05 },
                  { c: "#fff5d6", dx: -55, dy: -32, dr: -20, d: 0.1 },
                  { c: "#E8A93B", dx: -36, dy: 22, dr: 45, d: 0.15 },
                  { c: "#ffb27e", dx: 84, dy: -18, dr: 25, d: 0 },
                  { c: "#E8A93B", dx: 72, dy: 18, dr: -45, d: 0.08 },
                  { c: "#fff5d6", dx: 56, dy: -28, dr: 60, d: 0.12 },
                  { c: "#E63946", dx: 40, dy: 24, dr: -15, d: 0.18 },
                  { c: "#E8A93B", dx: -100, dy: -8, dr: -30, d: 0.2 },
                  { c: "#ffb27e", dx: 96, dy: 6, dr: 60, d: 0.22 },
                ].map((p, k) => (
                  <span
                    key={k}
                    style={{
                      background: p.c,
                      animationDelay: `${p.d}s`,
                      "--cx": `${p.dx}px`,
                      "--cy": `${p.dy}px`,
                      "--cr": `${p.dr}deg`,
                    }}
                  />
                ))}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "var(--bg-sunken)", border: "1px solid var(--line)",
                display: "grid", placeItems: "center",
                fontFamily: "var(--mono)", fontSize: 11, fontWeight: 700, color: "var(--fg-mute)",
                flexShrink: 0,
              }}>
                {d.name.slice(0, 1).toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, overflowWrap: "anywhere" }}>
                  {isNew && <span className="badge-nuevo">¡Nuevo!</span>}
                  <span>{d.name}</span>
                </div>
                {d.message && <div style={{ fontSize: 12, color: "var(--fg-dim)", marginTop: 2, fontStyle: "italic", overflowWrap: "anywhere" }}>"{d.message}"</div>}
              </div>
            </div>
            <div style={{ fontSize: 13, color: "var(--fg-mute)" }}>
              {CHANNEL_LABELS[d.method] || d.method}
            </div>
            <div style={{ fontFamily: "var(--mono)", fontWeight: 700, color: "var(--gold)", fontSize: 14, textAlign: "right" }}>
              ${fmtCLP(d.amount)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// useCampaign — estado simulado de la campaña con donaciones en vivo
// ────────────────────────────────────────────────────────────
const SEED_DONATIONS = [
  { name: "Constructora Pacífico SpA", amount: 5_000_000, from: "Quilpué", method: "transfer", message: null, dt: 3 * 60 * 1000 },
  { name: "María Elena R.",           amount: 25_000,    from: "El Belloto", method: "webpay", message: "Gracias por su valentía", dt: 8 * 60 * 1000 },
  { name: "Anónimo",                  amount: 10_000,    from: "Quilpué", method: "mach", message: null, dt: 14 * 60 * 1000 },
  { name: "Cond. Edif. Las Palmas",   amount: 1_200_000, from: "Quilpué", method: "transfer", message: "De parte de los 84 deptos", dt: 22 * 60 * 1000 },
  { name: "Pedro Andrade O.",         amount: 50_000,    from: "Villa Alemana", method: "khipu", message: null, dt: 45 * 60 * 1000 },
  { name: "Farmacia Don Lucho",       amount: 200_000,   from: "Quilpué Centro", method: "transfer", message: null, dt: 95 * 60 * 1000 },
  { name: "Camila S.",                amount: 5_000,     from: "Quilpué", method: "mach", message: "Para que lleguen más alto ❤️", dt: 130 * 60 * 1000 },
  { name: "Anónimo",                  amount: 100_000,   from: "Belloto Sur", method: "webpay", message: null, dt: 180 * 60 * 1000 },
  { name: "Colegio San Agustín",      amount: 480_000,   from: "Quilpué", method: "transfer", message: "Junta de apoderados", dt: 240 * 60 * 1000 },
  { name: "Juan Carlos M.",           amount: 20_000,    from: "Quilpué", method: "mp", message: null, dt: 320 * 60 * 1000 },
];

// Genera nombres "aleatorios" deterministas para nuevas donaciones
const RANDOM_NAMES = [
  "Catalina V.", "José Miguel P.", "Anónimo", "Familia Soto", "Anónimo",
  "Patricia L.", "Diego A.", "Anónimo", "Rosa Inés H.", "Comité Vecinal",
  "Anónimo", "Felipe O.", "Sra. Bertita", "Ferretería La Estrella", "Anónimo",
];
const RANDOM_FROM = ["Quilpué", "El Belloto", "Villa Alemana", "Quilpué Centro", "Belloto Sur", "Los Pinos"];
const RANDOM_AMOUNTS = [3000, 5000, 10000, 10000, 20000, 25000, 50000, 100000];

// Mapea una donación del backend ({id,name,amount_clp,origin,payment_method,anonymous,created_at,updated_at})
// a la forma que consumen los componentes del feed ({id,name,from,amount,method,message,at}).
// payment_method es el medio (transfer / mercadopago); origin queda de respaldo para filas antiguas.
function mapDonation(d) {
  return {
    id: d.id,
    name: d.name,
    from: d.from || "Quilpué",
    amount: d.amount_clp,
    method: d.payment_method || d.origin,
    message: null,
    at: d.updated_at ? Date.parse(d.updated_at) : (d.created_at ? Date.parse(d.created_at) : Date.now()),
  };
}

// bootstrap: undefined mientras el fetch consolidado (App) está en curso —
// no duplicar con un fetch propio. null si ese fetch falló o el caller no lo
// usa — mismo comportamiento de siempre (fetch propio inmediato). Objeto si
// ya llegaron los datos — se siembra el estado sin re-pedirlos.
function useCampaign({ goal = META_CLP, simulateLive = false, bootstrap = null } = {}) {
  const [raised, setRaised] = useStateS(0);
  const [goalClp, setGoalClp] = useStateS(goal);
  const [progress, setProgress] = useStateS(0);
  const [donors, setDonors] = useStateS(0);
  const [donations, setDonations] = useStateS([]);
  const [now, setNow] = useStateS(Date.now());
  const [lastDonor, setLastDonor] = useStateS(null);
  const [status, setStatus] = useStateS("active");
  const [mercadopagoFeeRate, setMercadopagoFeeRate] = useStateS(MERCADOPAGO_PROCESSING_FEE_RATE);

  // tick para "hace X min" — se pausa si la pestaña no está visible
  useEffectS(() => {
    let t = null;

    function start() {
      if (t) return;
      setNow(Date.now());
      t = setInterval(() => setNow(Date.now()), 1000);
    }

    function stop() {
      if (!t) return;
      clearInterval(t);
      t = null;
    }

    function sync() {
      if (isPageVisible()) start();
      else stop();
    }

    sync();
    const unsubscribe = onPageVisibilityChange(sync);
    return () => {
      stop();
      unsubscribe();
    };
  }, []);

  // Carga inicial + poll cada 30s (mismo TTL que Cache-Control de la API).
  // Si la pestaña está oculta no pegamos a la API; al volver, recargamos ya.
  const prevFirstId = useRefS(null);

  function applyCampaignPayload(c, donationsJson) {
    setGoalClp(c.goal_clp);
    setRaised(c.raised_clp);
    setProgress(c.progress);
    setDonors(c.donations_count);
    setStatus(c.status);
    setMercadopagoFeeRate(c.mercadopago_processing_fee_rate || MERCADOPAGO_PROCESSING_FEE_RATE);
    const mapped = donationsJson.map(mapDonation);
    const first = mapped[0];
    if (first && first.id !== prevFirstId.current) {
      setLastDonor({ id: first.id, name: first.name, amount: `$${fmtCLP(first.amount)}` });
      prevFirstId.current = first.id;
    }
    setDonations(mapped);
  }

  // Siembra el estado con los datos del fetch consolidado (App), sin pedirlos de nuevo.
  useEffectS(() => {
    if (!bootstrap) return;
    applyCampaignPayload(bootstrap.campaign, bootstrap.donations);
  }, [bootstrap]);

  // Poll cada 30s (mismo TTL que Cache-Control de la API). Si no hay bootstrap
  // (o falló), también hace la carga inicial por su cuenta como antes.
  useEffectS(() => {
    if (bootstrap === undefined) return; // esperando el fetch consolidado de App

    let cancelled = false;
    let timer;

    async function load() {
      if (!isPageVisible()) {
        // eslint-disable-next-line no-console
        console.log("[campaign] tab hidden: skipped API poll");
        return;
      }
      try {
        const { fetchCampaign, fetchDonations } = await import("../lib/api.js");
        const [c, d] = await Promise.all([fetchCampaign(), fetchDonations()]);
        if (cancelled) return;
        applyCampaignPayload(c, d);
      } catch (e) {
        // backend no disponible: se mantienen los contadores en cero
        // eslint-disable-next-line no-console
        console.error("No se pudo cargar la campaña", e);
      }
    }

    if (!bootstrap) load();
    timer = setInterval(load, 30_000);
    const unsubscribe = onPageVisibilityChange(() => {
      if (isPageVisible()) load();
    });
    return () => {
      cancelled = true;
      clearInterval(timer);
      unsubscribe();
    };
  }, [bootstrap === undefined]);

  // Transferencia: POST a la API. Tarjeta: redirige a Mercado Pago.
  async function donate(payload) {
    const isCompany = payload.kind === "company";

    const donation = {
      amount_clp: payload.amount,
      donor_name: isCompany ? payload.businessName : (payload.name || null),
      anonymous: isCompany ? false : !!payload.anonymous,
    };

    if (payload.paymentMethod === "card") {
      const { createMercadoPagoCheckout } = await import("../lib/api.js");
      const { init_point } = await createMercadoPagoCheckout({
        donation: {
          ...donation,
          donor_email: isCompany ? payload.contactEmail : payload.email,
        },
        personContribution: isCompany ? null : { contact_email: payload.email },
        companyContribution: isCompany
          ? {
              business_name: payload.businessName,
              business_rut: payload.businessRut,
              business_line: payload.businessLine,
              business_address: payload.businessAddress,
              legal_rep_name: payload.legalRepName,
              legal_rep_run: payload.legalRepRun,
              contact_email: payload.contactEmail,
              contact_phone: payload.contactPhone,
            }
          : null,
        coverProcessingFee: payload.coverProcessingFee,
      });
      window.location.href = init_point;
      return;
    }

    const { postDonation } = await import("../lib/api.js");

    if (isCompany) {
      return postDonation({
        donation,
        companyContribution: {
          payment_method: payload.paymentMethod,
          business_name: payload.businessName,
          business_rut: payload.businessRut,
          business_line: payload.businessLine,
          business_address: payload.businessAddress,
          legal_rep_name: payload.legalRepName,
          legal_rep_run: payload.legalRepRun,
          contact_email: payload.contactEmail,
          contact_phone: payload.contactPhone,
          receipt_reference: payload.receiptReference,
          receipt_file: payload.receiptFile,
        },
      });
    }

    return postDonation({
      donation,
      personContribution: {
        payment_method: payload.paymentMethod,
        contact_email: payload.email,
        receipt_reference: payload.receiptReference,
        receipt_file: payload.receiptFile,
      },
    });
  }

  return { raised, goal: goalClp, progress, donors, donations, now, lastDonor, donate, status, daysLeft: daysUntilJan15(now), mercadopagoFeeRate };
}

export {
  fmtCLP, fmtCLPshort,
  META_CLP, META_HEIGHT_M, FLOORS_TOTAL,
  heightCmFromAmount, fmtHeight,
  AnimatedNumber, MetaCounter,
  LiveTicker, LiveDonations,
  useCampaign,
};
